import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronLeft, LuChevronRight, LuLoaderCircle, LuSave, LuTriangleAlert } from 'react-icons/lu';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import Modal from '../ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';

const FIRST_FIELD = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';

/**
 * Dialog frame of the portal's long forms (employee, project). The fields are split into sections
 * (a list on the left, chips on phones), so each one fits without scrolling.
 * Enter moves to the next section and saves on the last one; Ctrl/⌘+Enter
 * saves from anywhere. Closing with unsaved changes asks first.
 */
const SectionedFormShell = ({
  title,
  subtitle,
  icon,
  formId,
  sections,
  activeSection,
  onSectionChange,
  sectionErrors,
  hasErrors,
  onSubmit,
  saving,
  submitLabel,
  dirty,
  onClose,
  children,
}) => {
  const { t, language } = useTranslation();
  const [confirmingClose, setConfirmingClose] = useState(false);
  const formRef = useRef(null);
  const contentRef = useRef(null);
  const index = Math.max(0, sections.findIndex((section) => section.id === activeSection));
  const section = sections[index];
  const lastIndex = sections.length - 1;

  const goTo = (target) => onSectionChange(sections[Math.min(Math.max(target, 0), lastIndex)].id);

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmingClose(true);
    else onClose();
  };

  // Keyboard and mouse users start in the section's first field; touch
  // screens don't, as focusing would pop up the keyboard.
  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return undefined;
    const frame = requestAnimationFrame(() => {
      contentRef.current?.querySelector(FIRST_FIELD)?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeSection]);

  const handleKeyDown = (event) => {
    // Already handled by a field (e.g. picking an option in a searchable select)
    if (event.key !== 'Enter' || event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
      return;
    }
    const tag = event.target.tagName;
    if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
    if (index < lastIndex) {
      event.preventDefault();
      goTo(index + 1);
    }
  };

  const footer = confirmingClose ? (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2">
      <p className="tw-mr-auto tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-amber-700 dark:tw-text-amber-300">
        <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
        {t('dataTable.form.unsaved')}
      </p>
      <Button variant="ghost" onClick={() => setConfirmingClose(false)}>{t('dataTable.form.keepEditing')}</Button>
      <Button variant="danger" onClick={onClose}>{t('dataTable.form.discard')}</Button>
    </div>
  ) : (
    <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
      <div className="tw-mr-auto tw-flex tw-items-center tw-gap-1">
        <IconButton
          label={t('dataTable.form.back')}
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          className="disabled:tw-pointer-events-none disabled:tw-opacity-40"
        >
          <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        </IconButton>
        <span className="tw-min-w-[2.75rem] tw-text-center tw-text-xs tw-font-semibold tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
          {t('dataTable.form.stepOf', { current: index + 1, total: sections.length })}
        </span>
        <IconButton
          label={t('dataTable.form.nextSection')}
          onClick={() => goTo(index + 1)}
          disabled={index === lastIndex}
          className="disabled:tw-pointer-events-none disabled:tw-opacity-40"
        >
          <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </IconButton>
      </div>
      <Button variant="ghost" onClick={requestClose} disabled={saving} className="max-sm:tw-hidden">
        {t('dataTable.form.cancel')}
      </Button>
      <Button variant="primary" type="submit" form={formId} disabled={saving}>
        {saving
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {saving ? t('dataTable.form.saving') : submitLabel}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={requestClose}
      size="form"
      title={title}
      subtitle={subtitle}
      icon={icon}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-0"
      footer={footer}
    >
      <form
        ref={formRef}
        id={formId}
        noValidate
        onSubmit={onSubmit}
        onKeyDown={handleKeyDown}
        className="md:tw-grid md:tw-min-h-[27rem] md:tw-grid-cols-[13rem_minmax(0,1fr)]"
      >
        <nav
          aria-label={t('dataTable.form.sections')}
          className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-gap-1.5 tw-overflow-x-auto tw-border-b tw-border-slate-200/80 tw-bg-white/95 tw-px-4 tw-py-3 [scrollbar-width:none] md:tw-static md:tw-flex-col md:tw-gap-1 md:tw-overflow-visible md:tw-border-b-0 md:tw-border-r md:tw-bg-slate-50/70 md:tw-px-3 md:tw-py-4 dark:tw-border-white/10 dark:tw-bg-[#1f232b]/95 md:dark:tw-bg-white/[0.02]"
        >
          {sections.map((item) => {
            const selected = item.id === section.id;
            const errorCount = sectionErrors[item.id] || 0;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSectionChange(item.id)}
                aria-current={selected ? 'step' : undefined}
                className={`tw-flex tw-shrink-0 tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 md:tw-w-full ${
                  selected
                    ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm tw-ring-1 tw-ring-slate-900/[0.06] dark:tw-bg-white/[0.08] dark:tw-text-white dark:tw-ring-white/10'
                    : 'tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-900/[0.04] hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.05] dark:hover:tw-text-white'
                }`}
              >
                <span className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg ${selected ? item.tint : 'tw-bg-slate-900/[0.05] tw-text-slate-400 dark:tw-bg-white/[0.06] dark:tw-text-slate-500'}`}>
                  <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                </span>
                <span className="tw-whitespace-nowrap md:tw-flex-1">{item.label}</span>
                {errorCount > 0 && (
                  <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">
                    {errorCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div ref={contentRef} className="tw-min-w-0 tw-p-5 sm:tw-p-6">
          <div className="tw-mb-5">
            <h3 className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{section.label}</h3>
            <p className="tw-mt-0.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{section.hint}</p>
          </div>
          {hasErrors && (
            <div role="alert" className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-rose-700 dark:tw-text-rose-300">
              <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
              {t('dataTable.form.fixErrors')}
            </div>
          )}
          {children}
        </div>
      </form>
    </Modal>
  );
};

SectionedFormShell.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  formId: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    tint: PropTypes.string.isRequired,
  })).isRequired,
  activeSection: PropTypes.string.isRequired,
  onSectionChange: PropTypes.func.isRequired,
  sectionErrors: PropTypes.objectOf(PropTypes.number).isRequired,
  hasErrors: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  submitLabel: PropTypes.string.isRequired,
  dirty: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default SectionedFormShell;
